import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services/api';
import PageHeader from '../components/PageHeader';
import toast from 'react-hot-toast';
import { Building2, FileText, RefreshCw, Search } from 'lucide-react';
import { Skeleton } from '../ui/kit';
import { datumUndZeit, tagSchluessel } from '../lib/zeit';

// GELD-SICHTBARKEIT-001 Teil B — eigener Bereich „Rechnungen" (Owner-Vorgabe 14.08.2026):
// Liste aller Fahrten mit ihren Belegen; Klick öffnet das PDF über den BESTEHENDEN,
// berechtigungsgeprüften und protokollierten Weg GET /finance/documents/:id.
// Eine Fahrt kann ZWEI Belege haben (Rechnung + Trinkgeldbeleg) — jede Belegzeile
// erscheint einzeln, es geht keiner verloren.

// RECHNUNGEN-BETRAG-UND-TAG-001: ÖRTLICHER Kalendertag, nicht der UTC-Tag.
// `toISOString().slice(0,10)` liefert zwischen 00:00 und 02:00 Berliner Zeit noch GESTERN —
// dann stünde „bis heute" auf gestern und die heutigen Belege fehlten wieder, obwohl der
// Server-Filter korrekt ist. `en-CA` ist die Sprachkennung, deren Datumsform JJJJ-MM-TT ist;
// gerechnet wird in der Zeitzone des Browsers, also derselben, in der die Liste die Uhrzeit
// anzeigt und in der das Buch seine Tage führt (Europe/Berlin).
const dayString = (date) => tagSchluessel(date);
const eur = (n) => (n == null ? '–' : Number(n).toFixed(2).replace('.', ',') + ' €');
const dt = (value) => datumUndZeit(value, { jahr: 'voll', leer: '–' });

const KIND_STYLE = {
  passenger_invoice: { color: '#0a0a0a', bg: '#e5e7eb' },
  tip_receipt: { color: '#7B1FA2', bg: '#f3e5f5' },
  cancellation_fee_receipt: { color: '#92400e', bg: '#fef3c7' },
  no_show_fee_receipt: { color: '#92400e', bg: '#fef3c7' },
};

const thStyle = { textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#4b5563', textTransform: 'uppercase', letterSpacing: 0.5, padding: '8px 10px', borderBottom: '1px solid #e5e7eb' };
const tdStyle = { fontSize: 13, color: '#0a0a0a', padding: '10px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' };
const inp = { padding: '8px 12px', background: '#f3f4f6', border: '1.5px solid #d1d5db', borderRadius: 9, color: '#0a0a0a', fontSize: 13, outline: 'none', fontFamily: 'inherit' };

function KindBadge({ kind, label }) {
  const style = KIND_STYLE[kind] || { color: '#374151', bg: '#f3f4f6' };
  return (
    <span style={{ display: 'inline-block', fontSize: 12, fontWeight: 700, color: style.color, background: style.bg, borderRadius: 8, padding: '3px 9px', whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
}

export default function Invoices() {
  const [from, setFrom] = useState(() => dayString(new Date(Date.now() - 30 * 86400000)));
  const [to, setTo] = useState(() => dayString(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(null);       // document_id, das gerade lädt
  const [rideQuery, setRideQuery] = useState('');
  const [rideCheck, setRideCheck] = useState(null);   // Antwort von /rides/:id/finance-documents
  const [checkingRide, setCheckingRide] = useState(false);
  const objectUrls = useRef([]);

  const load = useCallback(async (range) => {
    setLoading(true);
    try {
      const r = await api.get('/admin/finance-documents', { params: { from: range.from, to: range.to } });
      setData(r.data);
    } catch {
      toast.error('Belegliste konnte nicht geladen werden');
    } finally { setLoading(false); }
  }, []);

  // Initiale Ladung einmalig; danach lädt ausschließlich der „Zeitraum anwenden"-Knopf neu.
  useEffect(() => { load({ from, to }); }, [load]); // eslint-disable-line
  useEffect(() => () => { objectUrls.current.forEach((url) => window.URL.revokeObjectURL(url)); }, []);

  // Öffnen über den bestehenden Weg /finance/documents/:id — Berechtigungsprüfung und
  // Zugriffsprotokoll laufen dort; hier gibt es bewusst keinen zweiten Downloadweg.
  const openDocument = async (doc) => {
    if (!doc.available) {
      toast.error(`Beleg ${doc.document_number} ist nicht freigegeben (${doc.blocked_code || 'gesperrt'})`);
      return;
    }
    setOpening(doc.id);
    try {
      const response = await api.get(`/finance/documents/${doc.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      objectUrls.current.push(url);
      window.open(url, '_blank', 'noopener');
    } catch {
      toast.error('Beleg konnte nicht geöffnet werden');
    } finally { setOpening(null); }
  };

  const checkRide = async (event) => {
    event.preventDefault();
    const rideId = rideQuery.trim();
    if (!rideId) return;
    setCheckingRide(true);
    setRideCheck(null);
    try {
      const r = await api.get(`/admin/rides/${encodeURIComponent(rideId)}/finance-documents`);
      setRideCheck(r.data);
    } catch (error) {
      setRideCheck({ error: error?.response?.status === 404 ? 'Fahrt nicht gefunden' : 'Prüfung fehlgeschlagen' });
    } finally { setCheckingRide(false); }
  };

  const documentRow = (doc, extra = {}) => (
    <tr key={doc.id} className="invoices-row" style={{ cursor: doc.available ? 'pointer' : 'not-allowed', opacity: doc.available ? 1 : 0.55 }}
      title={doc.available ? 'Klick öffnet das PDF' : `nicht freigegeben (${doc.blocked_code || 'gesperrt'})`}
      onClick={() => openDocument(doc)}>
      <td style={tdStyle}>{dt(doc.occurred_at || doc.created_at)}</td>
      <td style={tdStyle}>
        <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12, color: '#4b5563' }}>{(extra.ride_id || doc.ride_id || '–')}</div>
        {(extra.pickup_address || doc.pickup_address) && (
          <div style={{ fontSize: 12, color: '#6b7280', maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {(extra.pickup_address || doc.pickup_address)} → {(extra.dropoff_address || doc.dropoff_address)}
          </div>
        )}
      </td>
      <td style={tdStyle}>{extra.customer_name || doc.customer_name || '–'}</td>
      <td style={{ ...tdStyle, fontWeight: 700, whiteSpace: 'nowrap' }}>{eur(doc.amount)}</td>
      <td style={tdStyle}><KindBadge kind={doc.kind} label={doc.kind_label} /></td>
      <td style={{ ...tdStyle, fontFamily: 'ui-monospace, monospace', fontSize: 12, whiteSpace: 'nowrap' }}>
        {doc.document_number}
        {opening === doc.id && <span style={{ marginLeft: 8, fontSize: 12, color: '#4b5563' }}>öffnet…</span>}
      </td>
    </tr>
  );

  return (
    <div className="invoices-page" style={{ maxWidth: 1100 }}>
      <PageHeader
        title="Rechnungen"
        description="Jede Fahrt mit Beleg — Klick öffnet das PDF (derselbe Beleg, den der Kunde bekommt). Der Abruf läuft über den berechtigungsgeprüften Dokumentweg und wird protokolliert."
      />

      {/* Fahrt-Prüfung — beantwortet ausdrücklich auch „kein Beleg vorhanden" (F-003) */}
      <form onSubmit={checkRide} style={{ display: 'flex', gap: 8, alignItems: 'center', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '12px 14px', marginBottom: 14 }}>
        <Search size={15} color="#4b5563" />
        <input value={rideQuery} onChange={(e) => setRideQuery(e.target.value)} placeholder="Fahrt-ID prüfen: hat diese Fahrt Belege?" style={{ ...inp, flex: 1 }} />
        <button type="submit" disabled={checkingRide || !rideQuery.trim()}
          style={{ padding: '8px 16px', background: '#0a0a0a', color: '#fff', borderRadius: 9, fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer', opacity: checkingRide || !rideQuery.trim() ? 0.6 : 1 }}>
          {checkingRide ? 'Prüft…' : 'Prüfen'}
        </button>
      </form>
      {rideCheck && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 14, marginBottom: 14 }}>
          {rideCheck.error ? (
            <div style={{ fontSize: 13, color: '#991b1b' }}>{rideCheck.error}</div>
          ) : (
            <>
              <div style={{ fontSize: 12, color: '#4b5563', marginBottom: 8 }}>
                Fahrt <span style={{ fontFamily: 'ui-monospace, monospace' }}>{rideCheck.ride.id}</span>
                {rideCheck.ride.customer_name ? ` · ${rideCheck.ride.customer_name}` : ''} · {dt(rideCheck.ride.completed_at || rideCheck.ride.created_at)}
              </div>
              {rideCheck.documents.length === 0 ? (
                <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 9, padding: '10px 12px' }}>
                  Für diese Fahrt existiert kein Beleg.
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>
                    <th style={thStyle}>Datum</th><th style={thStyle}>Fahrt</th><th style={thStyle}>Kunde</th>
                    <th style={thStyle}>Betrag</th><th style={thStyle}>Belegart</th><th style={thStyle}>Belegnummer</th>
                  </tr></thead>
                  <tbody>
                    {rideCheck.documents.map((doc) => documentRow(doc, {
                      ride_id: rideCheck.ride.id,
                      customer_name: rideCheck.ride.customer_name,
                      pickup_address: rideCheck.ride.pickup_address,
                      dropoff_address: rideCheck.ride.dropoff_address,
                    }))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      )}

      {/* Zeitraum-Filter */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '12px 14px', marginBottom: 14 }}>
        <div>
          <label style={{ fontSize: 12, color: '#4b5563', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 4 }}>Von</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={inp} />
        </div>
        <div>
          <label style={{ fontSize: 12, color: '#4b5563', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 4 }}>Bis</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={inp} />
        </div>
        <button type="button" onClick={() => load({ from, to })}
          style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px', background: '#0a0a0a', color: '#fff', borderRadius: 9, fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer' }}>
          <RefreshCw size={14} /> Zeitraum anwenden
        </button>
        {data && (
          <span style={{ fontSize: 12, color: '#4b5563', marginLeft: 'auto' }}>
            {data.total} Beleg{data.total === 1 ? '' : 'e'} im Zeitraum{data.truncated ? ` · ${data.shown} angezeigt (Zeitraum enger wählen)` : ''}
          </span>
        )}
      </div>

      {/* Beleg-Liste */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 6, overflowX: 'auto' }}>
        {loading ? <div style={{ padding: 12 }}><Skeleton h={160} r={10} /></div> : !data || data.documents.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 22, color: '#4b5563', fontSize: 13 }}>
            <FileText size={16} /> Keine Belege im gewählten Zeitraum.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={thStyle}>Datum</th><th style={thStyle}>Fahrt</th><th style={thStyle}>Kunde</th>
              <th style={thStyle}>Betrag</th><th style={thStyle}>Belegart</th><th style={thStyle}>Belegnummer</th>
            </tr></thead>
            <tbody>{data.documents.map((doc) => documentRow(doc))}</tbody>
          </table>
        )}
      </div>

      <p style={{ fontSize: 12, color: '#6b7280', marginTop: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Building2 size={12} /> Partnerabrechnung und VOOGO-Gebührenrechnung liegen weiterhin unter „Abrechnung".
      </p>
    </div>
  );
}
